import classNames from 'classnames';
import format from 'date-fns/format';

import CurrentSupplier from 'types/CurrentSupplier';
import Typography from '@components/atoms/Typography';
import ClockIcon from 'assets/icons/clock.svg';
import AvailableIcon from 'assets/icons/available.svg';
import { FormattedMessage } from 'utils/intl';

import classes from './ProductAvailability.module.scss';
import messages from './ProductAvailability.messages';

type ReleasedProductProps = {
  quantity: string;
  unavailable?: boolean;
  currentSupplier?: CurrentSupplier;
};

type Props = {
  variant?: 'normal' | 'picker';
} & (
  | ({
      isPrerelease: true;
    } & Partial<Record<keyof ReleasedProductProps, never>>)
  | ({
      isPrerelease?: false;
    } & ReleasedProductProps)
);

const ProductAvailability = ({
  quantity,
  unavailable = false,
  isPrerelease = false,
  variant = 'normal',
  currentSupplier,
}: Props): JSX.Element => {
  const isPicker = variant === 'picker';
  const icon = isPrerelease ? (
    <ClockIcon className={classes.icon} width={24} height={24} />
  ) : (
    <AvailableIcon className={classes.icon} />
  );
  const availabilityMessage = !isPicker ? messages.availability : messages.availabilityProduct;
  const unavailableMessage = <FormattedMessage {...(isPicker ? messages.productIsArriving : messages.soon)} />;
  const secondaryMessage = unavailable ? (
    unavailableMessage
  ) : (
    <FormattedMessage values={{ quantity }} {...messages.pcs} />
  );

  return (
    <div className={classes.container}>
      {isPicker && icon}
      <div
        className={classNames(classes.wrapper, {
          [classes.reversed]: isPicker && !unavailable,
        })}
      >
        <Typography
          className={classNames({
            [classes.availability]: !isPicker,
            [classes.availabilityPicker]: isPicker,
            [classes.isPrerelease]: isPrerelease,
          })}
          variant="body2"
          display="block"
        >
          {isPrerelease ? (
            <FormattedMessage {...messages.prereleaseProduct} />
          ) : (
            <FormattedMessage {...(isPicker && unavailable ? messages.askForAvailability : availabilityMessage)} />
          )}
        </Typography>
        {!currentSupplier?.availabilityTypeId ? (
          <Typography
            className={classNames({
              [classes.quantity]: !isPicker,
              [classes.quantityPicker]: isPicker,
              [classes.unavailable]: unavailable,
            })}
            variant="h2"
            renderAs="div"
            display="block"
          >
            {isPrerelease ? <FormattedMessage {...messages.availableSoon} /> : secondaryMessage}
          </Typography>
        ) : (
          <Typography
            variant="body1"
            renderAs="div"
            lineHeight="18px"
            display="block"
            className={classNames({
              [classes.availabilityGreen]: currentSupplier?.availabilityTimespan?.type === 'GREEN',
            })}
          >
            {currentSupplier.availabilityTypeId === 'ABSOLUTE' && currentSupplier.availabilityDate && (
              <FormattedMessage
                {...messages.from}
                values={{ date: format(new Date(currentSupplier.availabilityDate), 'dd.MM') }}
              />
            )}
            {currentSupplier.availabilityTypeId === 'RELATIVE' && (
              <FormattedMessage values={{ quantity }} {...messages.pcs} />
            )}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default ProductAvailability;
